import axiosClient, { pythonClient } from './axios.client.js';
import { getLangGraphBaseUrl } from '../config/brand.js';
import { toDisplayString } from '../utils/flowResultUtils';

const getPythonBackendUrl = () => getLangGraphBaseUrl();

/** Fetch flow execution status (Mongo-backed; used for resume-after-refresh). */
export const getFlowStatus = async (executionId) => {
  const pythonBackendUrl = getPythonBackendUrl();
  const headers = { Accept: 'application/json' };
  const token = typeof window !== 'undefined' ? localStorage.getItem('tkn') : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${pythonBackendUrl}/api/v1/flows/status/${encodeURIComponent(executionId)}`, { headers });
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, error: text || res.statusText || 'Invalid JSON' };
  }
};

// Get all flows for current user
export const getUserFlows = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    if (filters.tags) params.append('tags', filters.tags.join(','));

    const response = await axiosClient.get(`flows?${params.toString()}`);
    return { response };
  } catch (err) {
    console.error('Error fetching user flows:', err);
    return { err };
  }
};

// Get a specific flow
export const getFlow = async (flowId) => {
  try {
    const response = await axiosClient.get(`flows/${flowId}`);
    return { response };
  } catch (err) {
    console.error('Error fetching flow:', err);
    return { err };
  }
};

// Create a new flow
export const createFlow = async (flowData) => {
  try {
    const response = await axiosClient.post('flows', flowData);
    return { response };
  } catch (err) {
    console.error('Error creating flow:', err);
    return { err };
  }
};

// Update a flow
export const updateFlow = async (flowId, flowData) => {
  try {
    const response = await axiosClient.put(`flows/${flowId}`, flowData);
    return { response };
  } catch (err) {
    console.error('Error updating flow:', err);
    return { err };
  }
};

// Delete a flow
export const deleteFlow = async (flowId) => {
  try {
    const response = await axiosClient.delete(`flows/${flowId}`);
    return { response };
  } catch (err) {
    console.error('Error deleting flow:', err);
    return { err };
  }
};

// Execute a flow - Modified to use Python backend with async polling
export const executeFlow = async (flowId, input) => {
  try {
    // Get the flow definition from Node.js backend (API may return { flow } or raw flow)
    const { response: raw, err: flowErr } = await getFlow(flowId);
    if (flowErr || !raw) {
      throw new Error(flowErr?.message || 'Failed to fetch flow definition');
    }
    const flow = raw?.flow ?? raw;

    const pythonBackendUrl = getPythonBackendUrl();
    const requestInput = (typeof input === 'object' && input !== null && !Array.isArray(input))
      ? input
      : { input: input || 'Default input' };
    const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
    const res = await fetch(`${pythonBackendUrl}/execute-flow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        flow: {
          id: flow.id || flow._id,
          _id: flow.id || flow._id,
          name: flow.name,
          description: flow.description,
          nodes: flow.nodes || [],
          edges: flow.edges || [],
          globalState: flow.globalState || {},
          settings: flow.settings || {},
          state_schema: flow.state_schema || {},
          mcpContext: flow.mcpContext || {},
        },
        input: requestInput,
        user_id: flow.userId || flow.user_id || 'default_user',
        tenant_id: tenantId || undefined,
      }),
    });

    const data = await res.json();
    const executionId = data.execution_id;

    // Poll for status (pending = queued, running = executing, completed/paused = done)
    let { status } = data;
    let pollResult = null;
    let pollError = null;
    let pollData = null;
    let pollCount = 0;

    while (status === 'pending' || status === 'running' || (status === 'unknown' && pollCount < 5)) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      pollCount++;
      const pollRes = await fetch(`${pythonBackendUrl}/flow-status/${executionId}`);
      pollData = await pollRes.json();
      status = pollData.status;
      pollResult = pollData.result;
      pollError = pollData.error;
      if (pollCount > 30) break;
    }

    // Merge result with ai_response and variables for dashboard panels
    const finalResult = pollResult?.data ?? pollResult ?? pollError ?? {};
    const merged = typeof finalResult === 'object' && finalResult !== null
      ? { ...finalResult, ai_response: finalResult.ai_response ?? pollData?.ai_response, ...(pollData?.variables && Object.keys(pollData.variables).length ? pollData.variables : {}) }
      : finalResult;
    // Ensure ai_response is always a string to prevent [object Object]
    if (merged && typeof merged === 'object') {
      merged.ai_response = toDisplayString(merged.ai_response ?? merged.message ?? merged, '');
    }
    // When paused, include form fields from flow-status (top-level) for Debug Panel / form UI
    const pausedSource = pollData ?? data;
    if (status === 'paused' && pausedSource) {
      if (pausedSource.form_fields) merged.form_fields = pausedSource.form_fields;
      if (pausedSource.form_node_waiting) merged.form_node_waiting = pausedSource.form_node_waiting;
      if (pausedSource.form_title) merged.form_title = pausedSource.form_title;
      if (pausedSource.form_description) merged.form_description = pausedSource.form_description;
    }

    return {
      response: {
        result: merged,
        executionId,
        status,
      },
    };
  } catch (err) {
    console.error('Error executing flow:', err);
    return { err };
  }
};

// Execute agentic flow - Modified to use Python backend with async polling
export const executeAgenticFlow = async (flowId, input = {}) => {
  try {
    // Get the flow definition from Node.js backend (API may return { flow } or raw flow)
    const { response: raw, err: flowErr } = await getFlow(flowId);
    if (flowErr || !raw) {
      throw new Error(flowErr?.message || 'Failed to fetch flow definition');
    }
    const flow = raw?.flow ?? raw;

    const pythonBackendUrl = getPythonBackendUrl();
    const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
    const res = await fetch(`${pythonBackendUrl}/execute-flow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        flow: {
          name: flow.name,
          description: flow.description,
          nodes: flow.nodes,
          edges: flow.edges,
          globalState: flow.globalState,
          settings: flow.settings,
        },
        input: { input: input || 'Default input' },
        tenant_id: tenantId || undefined,
        user_id: flow.userId || 'default_user',
      }),
    });

    const data = await res.json();
    const executionId = data.execution_id;

    // Poll for status
    let { status } = data;
    let pollResult = null;
    let pollError = null;
    let pollCount = 0;

    while (status === 'pending' || status === 'running') {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      pollCount++;
      const pollRes = await fetch(`${pythonBackendUrl}/flow-status/${executionId}`);
      const pollData = await pollRes.json();
      status = pollData.status;
      pollResult = pollData.result;
      pollError = pollData.error;
      // Optionally, add a timeout or max poll count
      if (pollCount > 30) break; // 30*2s = 60s max
    }

    return {
      response: {
        result: pollResult?.data || pollResult || pollError || 'No response',
        executionId,
        status,
      },
    };
  } catch (err) {
    console.error('Error executing agentic flow:', err);
    return { err };
  }
};

// Fix: Ensure getAgenticFlowById is exported
export const getAgenticFlowById = async (flowId) => {
  try {
    // Validate flowId before making the request
    if (!flowId || flowId === 'undefined' || flowId === 'null') {
      return { err: { message: 'Invalid flow ID provided' } };
    }
    const response = await axiosClient.get(`flows/${flowId}`);
    return { response };
  } catch (err) {
    // If flow not found (404), clear the invalid ID from localStorage
    if (err?.status === 404) {
      localStorage.removeItem('agenticFlowBuilder_lastFlowId');
    }
    return { err };
  }
};

export const deleteAgenticFlow = async (flowId) => {
  try {
    // Validate flowId before making the request
    if (!flowId || flowId === 'undefined' || flowId === 'null') {
      return { err: { message: 'Invalid flow ID provided' } };
    }
    const response = await axiosClient.delete(`flows/${flowId}`);
    return { response };
  } catch (err) {
    return { err };
  }
};

// Fix: Ensure createAgenticFlow is exported
export const createAgenticFlow = async (flowData) => {
  try {
    // If updating, use PUT; if creating, use POST
    const method = flowData._id ? 'put' : 'post';
    const url = flowData._id ? `flows/${flowData._id}` : 'flows';

    // DEBUG: Log API call details
    console.log('🔍 DEBUG - API Call - Method:', method.toUpperCase());
    console.log('🔍 DEBUG - API Call - URL:', url);
    console.log('🔍 DEBUG - API Call - Flow Data Edges:', flowData.edges);
    console.log('🔍 DEBUG - API Call - Edges Count:', flowData.edges?.length || 0);
    console.log('🔍 DEBUG - API Call - Has AI Agent Edges:', flowData.edges?.some((e) => e.source && e.source.startsWith('ai-agent_')));

    const response = await axiosClient[method](url, flowData);

    // DEBUG: Log response
    console.log('🔍 DEBUG - API Response:', response);
    console.log('🔍 DEBUG - API Response Status:', response?.status);

    return { response };
  } catch (err) {
    // DEBUG: Log error
    console.error('🔍 DEBUG - API Error:', err);
    console.error('🔍 DEBUG - API Error Response:', err.response);
    return { err };
  }
};

// Get public flows (marketplace)
export const getPublicFlows = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.tags) params.append('tags', filters.tags.join(','));
    if (filters.search) params.append('search', filters.search);

    const response = await axiosClient.get(`flows/public/marketplace?${params.toString()}`);
    return { response };
  } catch (err) {
    console.error('Error fetching public flows:', err);
    return { err };
  }
};

// Validate flow structure
export const validateFlow = async (nodes, edges) => {
  try {
    const response = await axiosClient.post('flows/validate', { nodes, edges });
    return { response };
  } catch (err) {
    console.error('Error validating flow:', err);
    return { err };
  }
};

// Fix: Ensure getAgenticFlows is exported
export const getAgenticFlows = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    if (filters.tags) params.append('tags', filters.tags.join(','));
    if (filters.search) params.append('search', filters.search);

    const response = await axiosClient.get(`flows?${params.toString()}`);
    return { response };
  } catch (err) {
    console.error('Error fetching agentic flows:', err);
    return { err };
  }
};

// Workflow Templates
export const getWorkflowTemplates = async () => {
  try {
    const response = await axiosClient.get('flows/templates');
    return { response };
  } catch (err) {
    return { err };
  }
};

export const createWorkflowTemplate = async (templateData) => {
  try {
    const response = await axiosClient.post('flows/templates', templateData);
    return { response };
  } catch (err) {
    return { err };
  }
};

// Scheduled Workflows
export const getScheduledWorkflows = async () => {
  try {
    const response = await axiosClient.get('scheduled-flows');
    return { response };
  } catch (err) {
    return { err };
  }
};

export const createScheduledWorkflow = async (scheduleData) => {
  try {
    const response = await axiosClient.post('flows/schedules', scheduleData);
    return { response };
  } catch (err) {
    return { err };
  }
};

export const deleteScheduledWorkflow = async (scheduleId) => {
  try {
    const response = await axiosClient.delete(`flows/schedules/${scheduleId}`);
    return { response };
  } catch (err) {
    return { err };
  }
};

// Flow Monitoring
export const getFlowExecutions = async (flowId) => {
  try {
    const url = flowId ? `flow-executions/flow/${flowId}` : 'flow-executions';
    const response = await axiosClient.get(url);
    return { response };
  } catch (err) {
    return { err };
  }
};

export const getFlowExecutionLog = async (executionId) => {
  try {
    const response = await axiosClient.get(`flow-executions/${executionId}`);
    return { response };
  } catch (err) {
    return { err };
  }
};

// Automation Rules
export const getAutomationRules = async () => {
  try {
    const response = await axiosClient.get('automation-rules');
    return { response };
  } catch (err) {
    return { err };
  }
};

export const createAutomationRule = async (ruleData) => {
  try {
    const response = await axiosClient.post('flows/automation-rules', ruleData);
    return { response };
  } catch (err) {
    return { err };
  }
};

export const deleteAutomationRule = async (ruleId) => {
  try {
    const response = await axiosClient.delete(`flows/automation-rules/${ruleId}`);
    return { response };
  } catch (err) {
    return { err };
  }
};

// ====== NEW STREAMING AND ENHANCED FEATURES ======

/**
 * Execute a flow with real-time streaming updates using Server-Sent Events
 * @param {Object} flowData - The flow definition
 * @param {Object} input - Input data for the flow
 * @param {Function} onUpdate - Callback function for streaming updates
 * @param {Function} onError - Callback function for errors
 * @returns {EventSource} The EventSource connection for manual control
 */
export const executeFlowStreaming = async (flowData, input = {}, onUpdate, onError) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();

    // Prepare the request payload (support object input for filters etc.)
    const requestInput = (typeof input === 'object' && input !== null && !Array.isArray(input))
      ? input
      : { input: input || 'Default input' };
    const flowId = flowData.id || flowData._id;
    const flowIdStr = flowId ? String(flowId) : undefined;
    const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
    const payload = {
      flow: {
        id: flowIdStr,
        _id: flowIdStr,
        name: flowData.name,
        description: flowData.description,
        nodes: flowData.nodes || [],
        edges: flowData.edges || [],
        globalState: flowData.globalState || {},
        settings: flowData.settings || {},
        state_schema: flowData.state_schema || {},
        mcpContext: flowData.mcpContext || {},
      },
      input: requestInput,
      user_id: flowData.userId || 'default_user',
      tenant_id: tenantId || undefined,
    };

    console.log('🔄 Starting streaming execution for:', flowData.name);

    const streamUrl = `${pythonBackendUrl}/api/v1/flows/execute/stream`;
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    const token = typeof window !== 'undefined' ? localStorage.getItem('tkn') : null;
    if (token) headers.Authorization = `Bearer ${token}`;

    const streamResponse = await fetch(streamUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!streamResponse.ok) {
      let errMsg = `Streaming failed! status: ${streamResponse.status}`;
      try {
        const errBody = await streamResponse.text();
        if (errBody) {
          try {
            const parsed = JSON.parse(errBody);
            errMsg = parsed?.detail || parsed?.error || parsed?.message || errMsg;
          } catch {
            errMsg = errBody.slice(0, 200) || errMsg;
          }
        }
      } catch (_) {}
      throw new Error(errMsg);
    }

    // Create a manual SSE parser since we're using POST
    const reader = streamResponse.body.getReader();
    const decoder = new TextDecoder();
    const firstByteTimeoutMs = 15000; // If no data in 15s, treat as stream failure (proxy/backend may hang)
    let gotFirstByte = false;

    const processStream = async () => {
      try {
        while (true) {
          const readPromise = reader.read();
          const waitPromise = gotFirstByte
            ? readPromise
            : Promise.race([
                readPromise,
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Stream timeout: no data received in 15s')), firstByteTimeoutMs)
                ),
              ]);
          let result;
          try {
            result = await waitPromise;
          } catch (e) {
            if (e?.message?.includes('Stream timeout')) {
              console.warn('Stream returned no data in 15s, falling back to polling');
              if (onError) onError(e);
              return;
            }
            throw e;
          }
          const { done, value } = result;
          if (value?.length) gotFirstByte = true;
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('📡 Streaming update:', data.type, data.node_id);

                if (onUpdate) {
                  onUpdate(data);
                }

                // Auto-close on completion or error
                if (data.type === 'flow_complete' || data.type === 'flow_error') {
                  reader.cancel();
                  return;
                }
              } catch (parseError) {
                console.warn('Failed to parse streaming data:', parseError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Streaming error:', error);
        if (onError) {
          onError(error);
        }
      }
    };

    // Start processing the stream
    processStream();

    // Return a control object similar to EventSource
    return {
      close: () => {
        reader.cancel();
      },
      readyState: 1, // OPEN
    };
  } catch (error) {
    console.error('Error starting streaming execution:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Enhanced execute flow with streaming fallback to polling
 * This provides backward compatibility while enabling streaming when available
 */
export const executeFlowEnhanced = async (flowData, input = {}, options = {}) => {
  const {
    onUpdate,
    onError,
    onComplete,
    enableStreaming = true,
    fallbackToPolling = true,
  } = options;

  try {
    if (enableStreaming) {
      console.log('🔄 Starting enhanced execution with streaming...');

      return await executeFlowStreaming(
        flowData,
        input,
        (update) => {
          if (onUpdate) onUpdate(update);
          if (update.type === 'flow_complete' && onComplete) {
            onComplete(update);
          }
        },
        (error) => {
          if (onError) onError(error);

          // Fallback to polling if streaming fails
          if (fallbackToPolling) {
            console.warn('Streaming failed, falling back to polling...');
            return executeFlow(flowData.id || flowData._id, input);
          }
        },
      );
    }
    // Use traditional polling method
    console.log('🔄 Using traditional polling execution...');
    return await executeFlow(flowData.id || flowData._id, input);
  } catch (error) {
    console.error('Enhanced execution failed:', error);

    if (fallbackToPolling && enableStreaming) {
      console.warn('Falling back to polling due to error...');
      return await executeFlow(flowData.id || flowData._id, input);
    }

    throw error;
  }
};

// ========== Workflow Debug API ==========

/**
 * Execute flow in debug mode (pauses at breakpoints or every node)
 * @param {Object} flowData - Flow definition
 * @param {Object} input - Input data
 * @param {Object} options - { breakpoints: string[], pauseOnEveryNode: boolean }
 */
export const executeFlowDebug = async (flowData, input = {}, options = {}) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;
    const payload = {
      flow: {
        id: flowData.id || flowData._id,
        _id: flowData.id || flowData._id,
        name: flowData.name,
        description: flowData.description,
        nodes: flowData.nodes || [],
        edges: flowData.edges || [],
        globalState: flowData.globalState || {},
        settings: flowData.settings || {},
        state_schema: flowData.state_schema || {},
        mcpContext: flowData.mcpContext || {},
        breakpoints: flowData.breakpoints || [],
      },
      input: typeof input === 'object' && input !== null ? input : { input },
      user_id: flowData.userId || 'default_user',
      tenant_id: tenantId || undefined,
      debug_mode: true,
      breakpoints: options.breakpoints || flowData.breakpoints || [],
      pause_on_every_node: options.pauseOnEveryNode ?? false,
    };
    const res = await fetch(`${pythonBackendUrl}/execute-flow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.detail || 'Debug execution failed');
    return { success: true, execution_id: data.execution_id, status: data.status };
  } catch (err) {
    console.error('Error starting debug execution:', err);
    throw err;
  }
};

/**
 * Get full context at current breakpoint
 */
export const getDebugContext = async (executionId) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const res = await fetch(`${pythonBackendUrl}/api/v1/flows/executions/${executionId}/debug/context`, {
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to get debug context');
    return data;
  } catch (err) {
    console.error('Error getting debug context:', err);
    throw err;
  }
};

/**
 * Step to next node in debug mode
 */
export const debugStep = async (executionId) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const res = await fetch(`${pythonBackendUrl}/api/v1/flows/executions/${executionId}/debug/step`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Step failed');
    return data;
  } catch (err) {
    console.error('Error stepping:', err);
    throw err;
  }
};

/**
 * Run to end (disable breakpoints, continue to completion)
 */
export const debugRunToEnd = async (executionId) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const res = await fetch(`${pythonBackendUrl}/api/v1/flows/executions/${executionId}/debug/run-to-end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Run to end failed');
    return data;
  } catch (err) {
    console.error('Error running to end:', err);
    throw err;
  }
};

/**
 * Modify state at breakpoint then step (time travel)
 * @param {string} executionId
 * @param {Object} payload - { variables: {...} } or { state: {...} }
 */
export const debugModifyState = async (executionId, payload) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const res = await fetch(`${pythonBackendUrl}/api/v1/flows/executions/${executionId}/debug/modify-state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Modify state failed');
    return data;
  } catch (err) {
    console.error('Error modifying state:', err);
    throw err;
  }
};

/**
 * Stop debug execution
 */
export const debugStop = async (executionId) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const res = await fetch(`${pythonBackendUrl}/api/v1/flows/executions/${executionId}/debug/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Stop failed');
    return data;
  } catch (err) {
    console.error('Error stopping debug:', err);
    throw err;
  }
};

/**
 * Recover a failed flow execution from checkpoint
 */
export const recoverFlowExecution = async (executionId) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const response = await fetch(`${pythonBackendUrl}/api/v1/flows/recover/${executionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Recovery failed! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Flow recovery successful:', result);
    return result;
  } catch (error) {
    console.error('❌ Flow recovery failed:', error);
    throw error;
  }
};

/**
 * Get detailed execution metrics for a specific execution
 */
export const getExecutionMetrics = async (executionId) => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const response = await fetch(`${pythonBackendUrl}/api/v1/flows/monitoring/execution/${executionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get metrics! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('❌ Failed to get execution metrics:', error);
    throw error;
  }
};

/**
 * Get current system health and performance metrics
 */
export const getSystemHealth = async () => {
  try {
    const pythonBackendUrl = getPythonBackendUrl();
    const response = await fetch(`${pythonBackendUrl}/api/v1/flows/monitoring/system/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get system health! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('❌ Failed to get system health:', error);
    throw error;
  }
};

/**
 * Submit form data with streaming continuation
 * @param {string} executionId - The execution ID
 * @param {object} formData - The form data to submit
 * @param {function} onUpdate - Callback for streaming updates
 * @param {function} onError - Callback for errors
 */
export const submitFormStreaming = async (executionId, formData, onUpdate, onError) => {
  try {
    console.log('🔄 Starting streaming form submission for:', executionId);

    const pythonBackendUrl = getPythonBackendUrl();
    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
    const token = typeof window !== 'undefined' ? localStorage.getItem('tkn') : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${pythonBackendUrl}/api/v1/flows/submit-form/${encodeURIComponent(executionId)}/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ form_data: formData }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Streaming form submission failed! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const jsonData = line.slice(6); // Remove 'data: ' prefix
            if (jsonData.trim()) {
              const update = JSON.parse(jsonData);
              console.log('📡 Form streaming update:', update.type, update.execution_id);
              onUpdate(update);
            }
          } catch (parseError) {
            console.error('Error parsing streaming data:', parseError);
          }
        }
      }
    }

    return reader;
  } catch (error) {
    console.error('❌ Form streaming error:', error);
    onError(error);
    throw error;
  }
};
